import { useCallback, useMemo, useRef, useState } from 'react';
import _ from 'underscore';
import Promise from 'bluebird';

export const useCommonImageRepoMethods = ({
  imageRepository,
  logger,
  loadedImages,
  setLoadedImages,
}) => {
  const latestTimestampRef = useRef(0);
  const [imageCroppingTasks, setImageCroppingTasks] = useState({});
  // used to restart task status poller
  const [croppingTaskUpdated, setCroppingTaskUpdated] = useState(null);

  const getImageById = useCallback(({ imageId, useCache = true }) => {
    if (useCache) {
      const image = _.find(loadedImages, img => img.id === imageId);
      if (image) {
        return Promise.resolve(image);
      }
    }
    // no local value, request from backend
    return imageRepository.getImage(imageId);
  }, [loadedImages, imageRepository]);

  const getImageCroppingTaskStatus = useCallback((imageId) => {
    const requestTimestamp = Date.now();
    return imageRepository.getImageCroppingTaskStatus(imageId).then(({ tasks /* totalCount */ }) => {
      // if multipe requests are sent out, ensure to keep the result from the latest request
      if (requestTimestamp > latestTimestampRef.current) {
        // only update if any value is changed
        setImageCroppingTasks((curTasks) => {
          let dirty = false;
          const newTaks = (tasks || []).reduce((prev, tsk = {}) => {
            const curTsk = curTasks && curTasks[tsk.id];
            const newTsk = _.pick(tsk, ['id', 'stage', 'hasError', 'failureMessage', 'isFinished']) || {};
            if (newTsk.isFinished) {
              dirty = dirty || !!curTsk;
              // task is  finsished, remove it from the list
              return prev;
            }
            if (!curTsk || !_.isEqual(curTsk, newTsk)) {
              dirty = true;
            }
            return { ...prev, [newTsk.id]: newTsk };
          }, {});
          dirty = dirty || !_.isEmpty(_.difference(_.keys(curTasks), _.keys(newTaks)));
          if (dirty) {
            return newTaks;
          }
          return curTasks;
        });
        // eslint-disable-next-line no-param-reassign
        latestTimestampRef.current = requestTimestamp;
      }
      const taskCount = _.filter(tasks || [], t => t && !t.isFinished).length;
      return { totalCount: taskCount };
    }).catch((ex) => {
      // log and ignore the error
      logger.logError(`Failed to get image cropping task status. ${JSON.stringify(ex)}`);
    });
  }, [logger, imageRepository, latestTimestampRef]);

  const renameImage = useCallback(({ assetId: imageId, name }) => {
    const renameLocalImage = (images, setImages) => {
      const idx = _.findIndex(images, img => img.id === imageId);
      if (idx >= 0 && images[idx].name !== name) {
        const newImage = { ...images[idx], name };
        const newImages = [...images];
        newImages.splice(idx, 1, newImage);
        setImages(newImages);
      }
    };

    return imageRepository.renameImage({ imageId, name })
      .then(() => {
        logger.logTrace('DataService: image is renamed', { imageId, newName: name });
        renameLocalImage(loadedImages, setLoadedImages);
      })
      .tapCatch(() => {
        // logger.logError(err);
      });
  }, [imageRepository, logger, loadedImages, setLoadedImages]);

  const deleteImage = useCallback(({ assetId: imageId }) => {
    const promise = imageRepository.deleteImage(imageId)
      .tap(() => {
        logger.logTrace('DataService: image is deleted', { imageId });
      })
      .tapCatch(() => {
        // logger.logError();
      });
    return promise;
  }, [logger, imageRepository]);

  const cropImage = useCallback(({ imageId, cropSettings, placementSettings }) => {
    const ret = imageRepository.cropImage({ imageId, cropSettings, placementSettings }).then(() => {
      // update state to notify the status changes
      setCroppingTaskUpdated({ imageId });
      logger.logTrace('DataService: image is cropping', { imageId });
    });
    return ret;
  }, [logger, imageRepository]);

  const addCreative = useCallback(({ creativeData, baseId }) => {
    const ret = imageRepository.addCreative({ creativeData, baseId });
    return ret;
  }, [imageRepository]);

  const returnVal = useMemo(() => ({
    imageCroppingTasks,
    croppingTaskUpdated,
    getImageById,
    getImageCroppingTaskStatus,
    renameImage,
    deleteImage,
    cropImage,
    addCreative,
  }), [
    imageCroppingTasks,
    croppingTaskUpdated,
    getImageById,
    getImageCroppingTaskStatus,
    renameImage,
    deleteImage,
    cropImage,
    addCreative,
  ]);

  return returnVal;
};
